import { useEffect, useRef, useState, type RefObject } from 'react'

/**
 * Eleman kaydırma kabının görünür alanına yaklaştı mı — bir kez `true` olunca öyle kalır.
 *
 * Koleksiyon 200 karta kadar çiziyor (#147): her kart açılışta kafasını isteseydi ekranda
 * görünmeyen onlarca görsel aynı anda inerdi. Kart yaklaşınca istenir, bir kez indikten sonra
 * geri bırakılmaz (tekrar kaydırmada yeniden istek ve titreşim olmasın).
 *
 * `root` kaydırma kabıdır, viewport değil: iç içe kaydırmada `rootMargin` yalnız köke uygulanır;
 * kök viewport olsaydı kabın altında kalan kart payı hiç göremez, ancak ekrana girince yüklenirdi.
 * `IntersectionObserver` yoksa (eski tarayıcı, jsdom) hemen `true` — görsel kaybolmaz, yalnız
 * erteleme olmaz.
 */
export function useNearViewport<T extends Element>(
  root: Element | null,
  rootMargin = '50%',
): [RefObject<T | null>, boolean] {
  const ref = useRef<T>(null)
  const [near, setNear] = useState(() => typeof IntersectionObserver === 'undefined')

  useEffect(() => {
    const el = ref.current
    if (near || !el) return
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setNear(true)
          io.disconnect()
        }
      },
      { root, rootMargin },
    )
    io.observe(el)
    return () => io.disconnect()
  }, [near, root, rootMargin])

  return [ref, near]
}
